import React from 'react';
import {alpha, Box, CircularProgress, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip} from 'chart.js';
import {useObservable} from '@ngneat/react-rxjs';

import {StatisticsMode, statisticsQuery} from '@store/statistics';

import {Colors} from '@constants/colors.constants';
import {Bar} from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const EvolutionOfOperationsChart = () => {
  const { t } = useTranslation();

  const [filters] = useObservable(statisticsQuery.filters$);
  const [{ evolutionOfOperations, loading }] = useObservable(statisticsQuery.evolutionOfOperations$);

  return (
    <Stack flex={1} height="380px" bgcolor={Colors.white} borderRadius="4px" px="3px" py="20px">
      <Typography fontSize={18} fontWeight="700" ml="17px" mb="20px">
        {t('dashboard.statistics.operations.evolutionOfOperations.title')}
      </Typography>
      {loading && <CircularProgress size={30} style={{ alignSelf: 'center' }} />}
      {!loading && (
        <Box flex={1} mb="15px" width="100%">
          <Bar
            height="100%"
            options={{
              plugins: {
                legend: {display: false},
                title: {display: false},
                datalabels: {display: false},
                tooltip: {
                  backgroundColor: Colors.background,
                  displayColors: false,
                  cornerRadius: 4,
                  padding: 5,
                  xAlign: 'center',
                  yAlign: 'bottom',
                  caretSize: 0,
                  caretPadding: 10,
                  bodyColor: Colors.text,
                  bodyFont: {
                    family: 'CircularStd, sans-serif',
                    size: 14,
                    weight: '500',
                  },
                  callbacks: {
                    title: function () {
                      return '';
                    },
                  }
                },
              },
              maintainAspectRatio: false,
              interaction: {
                intersect: true,
              },
              scales: {
                x: {
                  grid: {
                    display: false,
                    drawBorder: false,
                    drawTicks: false,
                  },
                  ticks: {
                    color: Colors.text,
                    font: {
                      family: 'CircularStd, sans-serif',
                      size: 10,
                    },
                    backdropPadding: 0,
                    padding: 10,
                  },
                },
                y: {
                  beginAtZero: true,
                  grid: {
                    borderDash: [2],
                    color: alpha(Colors.chartBorder, 0.5),
                    drawBorder: false,
                    drawTicks: false,
                  },
                  ticks: {
                    color: Colors.text,
                    font: {
                      family: 'CircularStd, sans-serif',
                      size: 10,
                    },
                    backdropPadding: 0,
                    padding: 10,
                  },
                },
                y2: {
                  beginAtZero: true,
                  position: 'right',
                  grid: {
                    borderDash: [2],
                    color: alpha(Colors.chartBorder, 0.5),
                    drawBorder: false,
                    drawTicks: false,
                  },
                  ticks: {
                    color: Colors.text,
                    stepSize: 1, 
                    font: {
                      family: 'CircularStd, sans-serif',
                      size: 10,
                    },
                    backdropPadding: 0,
                    padding: 10,
                  },
                },
              },
            }}
            data={{
              labels: StatisticsMode.labels(filters),
              datasets: !loading ? [
                {
                  data: (evolutionOfOperations?.evolution.current?.map((it) => it.issueCount) || []),
                  backgroundColor: Colors.error,
                  borderRadius: 4,
                  categoryPercentage: 0.5,
                  stack: 'Stack 0',
                  yAxisID: 'y2',
                },
                {
                  data: (evolutionOfOperations?.evolution.current?.map((it) => it.execExecutedPickupCount) || []),
                  backgroundColor: Colors.text,
                  borderRadius: 4,
                  categoryPercentage: 0.5,
                  stack: 'Stack 1',
                },
              ] : [],
            }}/>
        </Box>
      )}
      <Stack
        bgcolor={Colors.background}
        borderRadius="4px"
        px="20px"
        py="10px"
        direction="row"
        alignItems="center"
        alignSelf="center"
        spacing={3}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box height="11px" width="11px" bgcolor={Colors.text} borderRadius="3px" />
          <Typography fontSize={12} fontWeight="400">
            {t('dashboard.statistics.operations.evolutionOfOperations.collects')}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box height="11px" width="11px" bgcolor={Colors.error} borderRadius="3px" />
          <Typography fontSize={12} fontWeight="400">
            {t('dashboard.statistics.operations.evolutionOfOperations.issues')}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default EvolutionOfOperationsChart;
